import './utils/console';

import React from 'react';
import ReactDOM from 'react-dom';
import objectAssign from 'object-assign';

import CONFIG from './config';

// import all the actions
import mapActions from './actions/map';
import zoomerActions from './actions/zoomer';

// import all the stores
import mapStore from './stores/map';
import zoomerStore from './stores/zoomer';
import StoreConnect from './flux/StoreConnect';

// import all the views
import SIMMainView from './components/main';
import SIMZoomerView from './components/zoomer';
import SIMMapView from './components/map';
import SIMSpinnerView from './components/spinner';

/**
 * This is the public API of the SIM library. It exports an object called **SIM** and,
 * in the bundled version, will output it as **window.SIM**
 */
var SIM = {};
export default SIM;

SIM.React = React;

/**
 * **SIM.actions** exposes the actions that the ISM requires to function
 *
 * You can both listen and emit
 *
 * @name SIM.actions
 * @type {{map: (exports|module.exports), tooltip: (exports|module.exports), zoomer: (exports|module.exports)}}
 */
SIM.actions = {map: mapActions, zoomer: zoomerActions};

/**
 * **SIM.stores** exposes the stores of the SIM Library
 * Use it in combination with [SIM.storeEnhance](./flux/storeenhance) to pass data to your view like this
 *
 * ```
 * import React, {Component} from 'react';
 * import {stores, storeEnhance} from 'sim';
 *
 * class MyView extends Component { ... }
 *
 * export default storeEnhance(MyView, {map: stores.map});
 *
 * ```
 * @name SIM.stores
 * @type {{map: (exports|module.exports), zoomer: (exports|module.exports)}}
 */
SIM.stores = {map: mapStore, zoomer: zoomerStore};

/**
 * **SIM.StoreConnect** is a higher order component that allows you hook your component to the SIM Stores
 *
 * @name SIM.StoreConnect
 * @type {function(Component, Object)}
 */
SIM.StoreConnect = StoreConnect;

/**
 * Main component to use in your application
 * @name SIM.View
 * @type {React.Component}
 */
SIM.View = SIM.wrappedView = SIMMainView;

SIM.Views = {
  Main:      SIMMainView,
  Zoomer:    SIMZoomerView,
  StaticMap: SIMMapView,
  Spinner:   SIMSpinnerView
};

SIM.config = CONFIG;

/**
 * Pass in the configuration that you want to change.
 * Only pass in the properties you want to update, it will be merged with the default config
 * @name SIM.setConfig
 * @param {Object} newConf the configuration that will override the [default config](config)
 */
SIM.setConfig = function (newConf) {
  // don't merge at a higher level, merge each key
  Object.keys(newConf).forEach(function (key) {
    var conf = SIM.config[key];
    if (conf === undefined) {
      SIM.config[key] = newConf[key];
    }
    else {
      objectAssign(SIM.config[key], newConf[key]);
    }
  });
};

/**
 * This function initializes the whole environment and renders the map view inside the
 * domNode passed into the function
 *
 * It is a wrapper around the default ReactJs Render function
 *
 * It also emits the *SIM.actions.map.EVENTS.LOAD* event that initializes the loading of
 * availability and geometry. Therefore be sure to set the configuration before calling
 * the render function
 *
 * ```javascript
 * // add the config first
 * var SIM = window.SIM;
 * SIM.setConfig({ ... });
 *
 * // render inside the #map div
 * SIM.render(document.getElementById('map'));
 * ```
 *
 * @name SIM.render
 * @param {Node} domNode
 */
SIM.render = function (domNode) {
  // validate its a valid Dom Node
  if (!(domNode instanceof window.Node)) {
    throw new Error('SIM.render: the render function requires a valid dom node parameter.');
  }
  ReactDOM.render(React.createElement(SIM.wrappedView), domNode);
  // Trigger the loading of the map only once the map is rendered in the dom
  SIM.actions.map.emit(SIM.actions.map.EVENTS.LOAD);
};

/**
 * This function is a helper function for when using SIM inside a react environment
 * Call this after rendering all of the application inside the DOM
 * It will load the data from the server
 *
 * @name SIM.init
 */
SIM.init = function () {
  // Trigger the loading of the map only once the map is rendered in the dom
  SIM.actions.map.emit(SIM.actions.map.EVENTS.LOAD);
};

/**
 * This function will return true/false if the map has a single seat
 *
 * @name SIM.hasSingleSeat
 * @return {Boolean}
 */
SIM.hasSingleSeat = () => mapStore.hasSingleSeat();



// WEBPACK FOOTER //
// ./frontend/react/sim/core/index.js